import { useMemo } from 'react';

/**
 * Build a Table-of-Contents from a lesson's HTML body.
 *
 * • Scans for every `<h2>` heading in the raw description HTML.
 * • Assigns each an anchor id (kebab-case slug of the text, deduped).
 * • Returns:
 *     - `html`: the SAME HTML with `id="..."` injected onto each h2 so
 *               anchor clicks / scrollIntoView work.
 *     - `toc`:  `[{ id, text }]` for the sidebar renderer.
 *
 * Pure — no DOM API used, safe to run during React render / SSR.
 * Falls back to the original html + empty toc when parsing fails.
 */
const slugify = (s) =>
  (s || 'section')
    .toLowerCase()
    .replace(/<[^>]+>/g, '')            // strip inner tags
    .replace(/[^a-z0-9\s\-]/g, '')      // drop punctuation
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 80) || 'section';

export const useLessonTOC = (rawHtml) => {
  return useMemo(() => {
    const html = rawHtml || '';
    if (!html || !/<h2[\s>]/i.test(html)) {
      return { html, toc: [] };
    }
    try {
      const toc = [];
      const seen = new Map();
      const injected = html.replace(
        /<h2([^>]*)>([\s\S]*?)<\/h2>/gi,
        (match, attrs, inner) => {
          // Only skip when the h2 already has an id AND we know its text.
          const existingId = /\bid=["']([^"']+)["']/.exec(attrs || '');
          const text = String(inner || '').replace(/<[^>]+>/g, '').trim();
          let id = existingId ? existingId[1] : slugify(text);
          if (!existingId) {
            const count = (seen.get(id) || 0) + 1;
            seen.set(id, count);
            if (count > 1) id = `${id}-${count}`;
          }
          toc.push({ id, text: text || `Section ${toc.length + 1}` });
          const nextAttrs = existingId ? attrs : `${attrs || ''} id="${id}"`;
          return `<h2${nextAttrs}>${inner}</h2>`;
        },
      );
      return { html: injected, toc };
    } catch (_err) {
      return { html, toc: [] };
    }
  }, [rawHtml]);
};

export default useLessonTOC;
